import { generateQueryString, IHttpResponse } from '@qlibs/react-components';
import { httpRequest } from '../../../helpers/api';
import { ILogReport } from '../types/report-log.type';

const SECONDS_PER_DAY = 86400;
const DEFAULT_CONCURRENCY = 2;

export const MAX_BATCH_DAYS = 7;
export const MAX_BATCHED_RANGE_DAYS = 31;

export interface IReportLogWindow {
  startAt: number;
  endAt: number;
}

export function buildReportLogWindows(
  startAt: number,
  endAt: number,
  maxDays: number = MAX_BATCH_DAYS
): IReportLogWindow[] {
  const span = maxDays * SECONDS_PER_DAY;
  const windows: IReportLogWindow[] = [];

  for (let start = startAt; start <= endAt; start += span) {
    windows.push({ startAt: start, endAt: Math.min(endAt, start + span - 1) });
  }

  return windows;
}

export function mergeReportLogPayloads(
  target: ILogReport,
  payload: ILogReport
): ILogReport {
  const merged = target as Record<string, Record<string, unknown>>;
  const source = (payload || {}) as Record<string, Record<string, unknown>>;

  for (const section of Object.keys(source)) {
    const value = source[section];

    if (!value || typeof value !== 'object') {
      continue;
    }

    merged[section] = { ...(merged[section] || {}), ...value };
  }

  return merged as ILogReport;
}

async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  let failed = false;

  const worker = async () => {
    while (next < tasks.length && !failed) {
      const index = next++;

      try {
        results[index] = await tasks[index]();
      } catch (error) {
        failed = true;
        throw error;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, worker)
  );

  return results;
}

export async function fetchReportLogBatched(params: {
  query: Record<string, unknown>;
  startAt: number;
  endAt: number;
  batch: boolean;
  signal?: AbortSignal;
  concurrency?: number;
}): Promise<ILogReport> {
  const windows = params.batch
    ? buildReportLogWindows(params.startAt, params.endAt)
    : [{ startAt: params.startAt, endAt: params.endAt }];

  const tasks = windows.map((window) => async () => {
    const query = {
      ...params.query,
      startAt: window.startAt,
      endAt: window.endAt,
    };
    const res = await httpRequest.get<IHttpResponse<ILogReport>>(
      `report-log${generateQueryString(query)}`,
      { signal: params.signal }
    );

    return res.data.payload;
  });

  const payloads = await runWithConcurrency(
    tasks,
    params.concurrency ?? DEFAULT_CONCURRENCY
  );

  return payloads.reduce<ILogReport>(
    (merged, payload) => mergeReportLogPayloads(merged, payload),
    {}
  );
}
